<script setup lang="ts">
import { ref, nextTick } from 'vue';
import type { AssigneeData } from 'src/services/assignee-service';
import { useDropdownState } from 'src/composables/useDropdownState';
import { useAssigneeService } from 'src/services/assignee-service';
import SharedDropdown from 'src/components/shared/SharedDropdown.vue';
import 'src/assets/ticket-edit-property.css';

defineProps<{
  assignee?: AssigneeData | null;
}>();

const emit = defineEmits<{
  'update:assignee': [value: AssigneeData | null];
}>();

const rootEl = ref<HTMLElement>();
const { isOpen, toggle, close } = useDropdownState(rootEl);

const { searchAssignees } = useAssigneeService();
const assigneeOptions = ref<{ id: string; name: string }[]>([]);
const assigneeQuery = ref('');
const assigneeSearchRef = ref<HTMLInputElement>();

async function loadAssignees(query?: string): Promise<void> {
  try {
    const response = await searchAssignees(query || null, 1, 25);
    assigneeOptions.value = (response?.data ?? []).map((a: any) => ({ id: a.id, name: a.name }));
  } catch {
    assigneeOptions.value = [];
  }
}

function openAssigneeDropdown(): void {
  toggle();
  if (isOpen.value) {
    loadAssignees();
    nextTick(() => assigneeSearchRef.value?.focus());
  }
}

function selectAssignee(a: { id: string; name: string }): void {
  emit('update:assignee', { id: a.id, name: a.name });
  close();
  assigneeQuery.value = '';
}

function clearAssignee(): void {
  emit('update:assignee', null);
  close();
}
</script>

<template>
  <div ref="rootEl" class="relative">
    <div class="prop-row" @click="openAssigneeDropdown">
      <span class="prop-label">Assignee</span>
      <span class="inline-flex items-center gap-2 text-s font-medium text-surface-700 dark:text-surface-300">
        <template v-if="assignee?.name">
          {{ assignee.name }}
        </template>
        <template v-else>
          Unassigned
        </template>
        <i class="pi pi-chevron-down prop-chevron" />
      </span>
    </div>
    <SharedDropdown :open="isOpen" max-height="300px" @click.stop>
      <div class="p-1.5 border-b border-surface-200 dark:border-surface-700">
        <input
          ref="assigneeSearchRef"
          v-model="assigneeQuery"
          type="text"
          placeholder="Search assignee..."
          class="w-full px-2 py-1.5 text-s bg-transparent border-none outline-none text-surface-700 dark:text-surface-300"
          @input="loadAssignees(assigneeQuery)"
        />
      </div>
      <button
        v-if="assignee?.id"
        class="te-dropdown-item text-surface-400"
        @click.stop="clearAssignee"
      >
        <i class="pi pi-times mr-2" style="font-size: 10px" />
        Unassign
      </button>
      <button
        v-for="a in assigneeOptions"
        :key="a.id"
        class="te-dropdown-item"
        :class="{ 'te-dropdown-item-active': a.id === assignee?.id }"
        @click.stop="selectAssignee(a)"
      >
        {{ a.name }}
      </button>
    </SharedDropdown>
  </div>
</template>
